import { VALUE_TYPES } from './data';
import { constructorName } from './helpers';
import { isArguments } from './isArguments';
import { isBuffer } from './isBuffer';
import { isDate } from './isDate';
import { isError } from './isError';
import { isGeneratorFunction, isGeneratorObject } from './isGenerator';
import { isRegExp } from './isRegExp';
import type { ValueType } from './types';
export type { ValueType } from './types';

/**
 * Determines the type of the given value
 */
export function typeOf(value: any): ValueType | string {
  if (value === void 0) {
    return VALUE_TYPES.undefined;
  }

  if (value === null) {
    return VALUE_TYPES.null;
  }

  let type: string = typeof value;

  if (type === 'boolean') {
    return VALUE_TYPES.boolean;
  }

  if (type === 'string') {
    return VALUE_TYPES.string;
  }

  if (type === 'number') {
    return VALUE_TYPES.number;
  }

  if (type === 'symbol') {
    return VALUE_TYPES.symbol;
  }

  if (type === 'function') {
    return isGeneratorFunction(value)
      ? VALUE_TYPES.generatorfunction
      : VALUE_TYPES.function;
  }

  if (Array.isArray(value)) {
    return VALUE_TYPES.array;
  }

  if (isBuffer(value)) {
    return VALUE_TYPES.buffer;
  }

  if (isArguments(value)) {
    return VALUE_TYPES.arguments;
  }

  if (isDate(value)) {
    return VALUE_TYPES.date;
  }

  if (isError(value)) {
    return VALUE_TYPES.error;
  }

  if (isRegExp(value)) {
    return VALUE_TYPES.regexp;
  }

  switch (constructorName(value)) {
    case 'Symbol': {
      return VALUE_TYPES.symbol;
    }
    case 'Promise': {
      return VALUE_TYPES.promise;
    }
    case 'WeakMap': {
      return VALUE_TYPES.weakmap;
    }
    case 'WeakSet': {
      return VALUE_TYPES.weakset;
    }
    case 'Map': {
      return VALUE_TYPES.map;
    }
    case 'Set': {
      return VALUE_TYPES.set;
    }
    case 'Int8Array': {
      return VALUE_TYPES.int8array;
    }
    case 'Uint8Array': {
      return VALUE_TYPES.uint8array;
    }
    case 'Uint8ClampedArray': {
      return VALUE_TYPES.uint8clampedarray;
    }
    case 'Int16Array': {
      return VALUE_TYPES.int16array;
    }
    case 'Uint16Array': {
      return VALUE_TYPES.uint16array;
    }
    case 'Int32Array': {
      return VALUE_TYPES.int32array;
    }
    case 'Uint32Array': {
      return VALUE_TYPES.uint32array;
    }
    case 'Float32Array': {
      return VALUE_TYPES.float32array;
    }
    case 'Float64Array': {
      return VALUE_TYPES.float64array;
    }
    case 'BigInt64Array': {
      return VALUE_TYPES.bigint64array;
    }
    case 'BigUint64Array': {
      return VALUE_TYPES.biguint64array;
    }
  }

  if (isGeneratorObject(value)) {
    return VALUE_TYPES.generator;
  }

  type = toString.call(value) as string;

  switch (type) {
    case '[object Object]': {
      return VALUE_TYPES.object;
    }
    case '[object Map Iterator]': {
      return VALUE_TYPES.mapiterator;
    }
    case '[object Set Iterator]': {
      return VALUE_TYPES.setiterator;
    }
    case '[object String Iterator]': {
      return VALUE_TYPES.stringiterator;
    }
    case '[object Array Iterator]': {
      return VALUE_TYPES.arrayiterator;
    }
  }

  return type.slice(8, -1).toLowerCase().replace(/\s/g, '');
}
